// TrackFormPage — dynamic interest form per track (5 of 6 tracks use this).
// On submit → backend computes 3-5 personalised recommendations → redirect to /dashboard.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import { fetchMyTrack, submitInterest } from '../lib/tracksApi';

export default function TrackFormPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trackData, setTrackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate('/signin', { replace: true }); return; }
    if (user.is_admin) { navigate('/dashboard', { replace: true }); return; }
    (async () => {
      try {
        const data = await fetchMyTrack();
        if (!data.selected_track) { navigate('/onboarding/track', { replace: true }); return; }
        if (!data.track_video_watched) { navigate('/onboarding/video', { replace: true }); return; }
        if (data.track?.post_signup === 'quiz') { navigate('/onboarding/quiz', { replace: true }); return; }
        if (data.interest_answers && data.onboarding_complete) {
          // Already done — prefill but don't block re-submit
          setAnswers(data.interest_answers);
        }
        setTrackData(data);
      } catch {
        setError('Could not load form.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const updateAnswer = (field, value) => {
    setAnswers((prev) => ({ ...prev, [field.name]: value }));
  };

  const toggleMulti = (field, opt) => {
    setAnswers((prev) => {
      const arr = Array.isArray(prev[field.name]) ? [...prev[field.name]] : [];
      const idx = arr.indexOf(opt);
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        if (field.max && arr.length >= field.max) {
          return prev;
        }
        arr.push(opt);
      }
      return { ...prev, [field.name]: arr };
    });
  };

  const validate = () => {
    const schema = trackData?.form_schema;
    if (!schema) return 'Form schema missing.';
    for (const f of schema.fields) {
      if (!f.required) continue;
      const v = answers[f.name];
      if (f.type === 'multi') {
        if (!Array.isArray(v) || v.length === 0) return `Pick at least one for: ${f.label}`;
      } else if (!v || (typeof v === 'string' && !v.trim())) {
        return `Please answer: ${f.label}`;
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    setError('');
    try {
      await submitInterest(answers);
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Could not save your answers. Try again.');
      setSubmitting(false);
    }
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600"/>
      </div>
    );
  }
  if (!trackData || !trackData.form_schema) return null;
  const { track, form_schema } = trackData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar/>
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/onboarding/video')} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="w-4 h-4"/> Back to video
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-medium mb-4">
              <Sparkles size={12}/> Step 2 of 2 · Personalisation
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{track?.name}</h1>
            <p className="text-gray-600">Answer these so we hand-pick the right 3–5 careers for you.</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-3xl shadow-xl p-6 md:p-8 space-y-6" data-testid="track-interest-form">
            {form_schema.fields.map((f, idx) => (
              <div key={f.name} data-testid={`track-form-field-${f.name}`}>
                <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                  <span className="text-teal-600 mr-1">{idx + 1}.</span> {f.label}
                  {!f.required && <span className="text-xs text-gray-400 ml-1">(optional)</span>}
                </Label>

                {f.type === 'radio' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {f.options.map((opt) => {
                      const selected = answers[f.name] === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => updateAnswer(f, opt)}
                          className={`text-left px-4 py-3 rounded-xl border text-sm transition-all ${selected ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-300 font-semibold text-teal-900' : 'border-gray-200 hover:border-gray-300'}`}
                          data-testid={`track-form-${f.name}-opt-${opt.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                        >
                          {selected && <Check className="inline w-4 h-4 mr-1 text-teal-600"/>}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}

                {f.type === 'multi' && (
                  <>
                    <p className="text-xs text-gray-500 mb-2">
                      {Array.isArray(answers[f.name]) ? answers[f.name].length : 0}{f.max ? ` / ${f.max}` : ''} selected
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                      {f.options.map((opt) => {
                        const arr = Array.isArray(answers[f.name]) ? answers[f.name] : [];
                        const selected = arr.includes(opt);
                        const reachedMax = f.max && arr.length >= f.max && !selected;
                        return (
                          <button
                            key={opt}
                            type="button"
                            disabled={reachedMax}
                            onClick={() => toggleMulti(f, opt)}
                            className={`text-left px-3 py-2 rounded-lg border text-xs transition-all ${selected ? 'border-teal-600 bg-teal-50 text-teal-900 font-semibold' : reachedMax ? 'border-gray-100 text-gray-300 cursor-not-allowed' : 'border-gray-200 hover:border-gray-300'}`}
                            data-testid={`track-form-${f.name}-multi-${opt.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                          >
                            {selected && <Check className="inline w-3 h-3 mr-1"/>}
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {f.type === 'text' && (
                  <Input
                    type="text"
                    value={answers[f.name] || ''}
                    onChange={(e) => updateAnswer(f, e.target.value)}
                    placeholder={f.placeholder || ''}
                    className="mt-2 h-11"
                    data-testid={`track-form-${f.name}-input`}
                  />
                )}
              </div>
            ))}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="track-form-error">
                {error}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="btn-primary"
                size="lg"
                data-testid="track-form-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
                Unlock my 3–5 careers
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
